import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import { toSEOSearchString } from '@travel/traveler-core/utils/seo';
import { isNotEmptyArray } from '@travel/utils';

import Breadcrumb from 'components/Breadcrumb';
import { getRequestBody } from 'pages/ProviderListPage/resolver';

import { pushLocation } from 'store/__router/actions';
import { getIsSSR } from 'store/__router/selectors';
import { getLimitationsForCurrentMarket } from 'store/markets/selectors';
import { fetchProviderFilters, updateMapExpansionState } from 'store/providerList/actions';
import {
  getFilters,
  getIsFetchingList,
  getIsMapExpanded,
  getPlaceLayers,
  getSelectedObjectivesFeatureIdMapping,
} from 'store/providerList/selectors';
import { getCouponDetails } from 'store/salesPromotion/selectors';

import { EMPTY_ARRAY } from 'constants/defaultValue';
import { FILTER_GROUP_IDS, SortKeysType } from 'constants/provider';
import paths from 'core/universalRouter/paths';
import useProviderFeatureFilter from 'hooks/useProviderFeatureFilter';
import { SearchFormQuery } from 'ProviderList-Types';

import usePageLoaderOnInitial from '../../hooks/usePageLoaderOnInitial';
import { PriceRangeType, toggleFilterOption } from './components/FilterContainer';
import FiltersAndList from './components/FiltersAndList';
import ListAndMap from './components/ListAndMap';
import { SearchFormRef } from './components/SearchForm';
import SearchFormWithRef from './components/SearchForm/SearchFormWithRef';
import { FIRST_PAGE_PARAMS } from './resolver';

import styles from './providerListPage.module.scss';

type Props = {
  query: SearchFormQuery;
  params: { pageIdentifier: string };
};

type ToggleOptionsType = {
  features?: string;
  maxPrice?: string;
  minPrice?: string;
  freeCancellation?: string;
  sortKey?: SortKeysType;
  'starRatings[]'?: string[];
  'reviewScores[]'?: string[];
};

function ProviderListPage(props: Props) {
  const { query, params } = props;

  const dispatch = useDispatch();
  const isMobile = useDeviceType() === MOBILE_SCREEN;
  const placeLayers = useSelector(getPlaceLayers);
  const isExpandedMap = useSelector(getIsMapExpanded);
  const objectiveFeatureMappingIds = useSelector(getSelectedObjectivesFeatureIdMapping);
  const filters = useSelector(getFilters);

  const couponId = props.query?.salesPromotionId;
  const couponDetail = useSelector(getCouponDetails(couponId));

  const searchFreeCancellation = query.freeCancellation === 'true' ? true : false;
  const searchMaxPrice = query.maxPrice ? Number(query.maxPrice) : null;
  const searchMinPrice = query.minPrice ? Number(query.minPrice) : null;
  const searchStarRating =
    (typeof query['starRatings[]'] === 'string'
      ? [query['starRatings[]']]
      : query['starRatings[]']) || EMPTY_ARRAY;
  const searchReviewRating =
    (typeof query['reviewScores[]'] === 'string'
      ? [query['reviewScores[]']]
      : query['reviewScores[]']) || EMPTY_ARRAY;

  const [maxPrice, setMaxPrice] = useState<PriceRangeType>(searchMaxPrice);
  const [minPrice, setMinPrice] = useState<PriceRangeType>(searchMinPrice);
  const [starRating, setStarRating] = useState<Array<string>>(searchStarRating);
  const [reviewRating, setReviewRating] = useState<Array<string>>(searchReviewRating);
  const [freeCancellation, setFreeCancellation] = useState(searchFreeCancellation);
  const [sortKey, setSortKey] = useState(props.query?.sortKey || 'recommended');
  const [newQuery, setNewQuery] = useState(query);
  const { features, setFeatures } = useProviderFeatureFilter(
    objectiveFeatureMappingIds,
    query.features,
  );

  const isSSR = useSelector(getIsSSR);

  const searchFormRef = useRef<SearchFormRef>(null);

  const recentPriceFilter = filters.find(filter => filter.groupId === FILTER_GROUP_IDS.PRICE);
  const appliedMin = minPrice ?? recentPriceFilter?.min;
  const appliedMax = maxPrice ?? recentPriceFilter?.max;

  const limitations = useSelector(getLimitationsForCurrentMarket);

  const onRemoveCoupon = useCallback(() => {
    dispatch(
      pushLocation({
        pathname: paths.providerList.pathResolver(params.pageIdentifier),
        search: toSEOSearchString(
          {
            ...query,
            salesPromotionId: undefined,
            isCouponSpecified: undefined,
            // reset page state
            ...FIRST_PAGE_PARAMS,
          },
          limitations,
        ),
      }),
    );
  }, [dispatch, params.pageIdentifier, query]);

  const toggleFeatureFilterOptions = useCallback(
    (newFeatures: string[]) => {
      let nextFeatures = [...features];

      if (isNotEmptyArray(newFeatures)) {
        const filteredFeatures = features.filter(feature => !newFeatures?.includes(feature));
        const newlyAddedFeatures = newFeatures.filter(feature => !features?.includes(feature));

        nextFeatures = [...filteredFeatures, ...newlyAddedFeatures];
      }

      setFeatures(nextFeatures);
      return nextFeatures;
    },
    [features, setFeatures],
  );

  const onFilterChange = (toggleOptions: ToggleOptionsType) => {
    const newQuery = {
      ...query,
      // faceting endpoint won't use min-max price anymore due to requirement change
      minPrice: '',
      maxPrice: '',
      features: features?.toString(),
      sortKey: sortKey,
      'starRatings[]': starRating,
      'reviewScores[]': reviewRating,
      freeCancellation: freeCancellation.toString(),
      ...toggleOptions,
    };

    dispatch(
      fetchProviderFilters(
        getRequestBody(
          {
            params: params,
            query: newQuery,
          },
          couponDetail,
        ),
      ),
    );
    setNewQuery(newQuery);
  };

  const onPriceRangeChanged = (min?: number, max?: number) => {
    if (Number(min) < 0) {
      return;
    }

    setMinPrice(min);
    setMaxPrice(max);
  };

  const onCheckboxChanged = (filterGroupId: string, toggleOption: string) => {
    switch (filterGroupId) {
      case FILTER_GROUP_IDS.SORT: {
        setSortKey(toggleOption as SortKeysType);
        onFilterChange({ sortKey: toggleOption as SortKeysType });
        break;
      }
      case FILTER_GROUP_IDS.RATING: {
        const nextRatings: Array<string> = toggleFilterOption(starRating, toggleOption);
        setStarRating(nextRatings);
        onFilterChange({ 'starRatings[]': nextRatings });
        break;
      }
      case FILTER_GROUP_IDS.REVIEW_SCORE: {
        const nextReviewScores: Array<string> = toggleFilterOption(reviewRating, toggleOption);
        setReviewRating(nextReviewScores);
        onFilterChange({ 'reviewScores[]': nextReviewScores });
        break;
      }
      case FILTER_GROUP_IDS.CANCELLATION_POLICY: {
        setFreeCancellation(!freeCancellation);
        onFilterChange?.({ freeCancellation: String(!freeCancellation) });
        break;
      }
      default: {
        const nextFeatures = toggleFeatureFilterOptions([toggleOption]);
        onFilterChange?.({ features: nextFeatures.toString() });
        break;
      }
    }
  };

  const onClearClick = () => {
    setMinPrice(null);
    setMaxPrice(null);
    setFeatures(EMPTY_ARRAY);
    setStarRating(EMPTY_ARRAY);
    setReviewRating(EMPTY_ARRAY);
    setSortKey('recommended');
    setFreeCancellation(false);

    onFilterChange({
      minPrice: '',
      maxPrice: '',
      features: '',
      sortKey: 'recommended',
      'starRatings[]': EMPTY_ARRAY,
      'reviewScores[]': EMPTY_ARRAY,
      freeCancellation: 'false',
    });
  };

  const onSearch = () => {
    searchFormRef.current?.onSubmit();
  };

  const onUpdateListView = useCallback(() => {
    dispatch(updateMapExpansionState(!isExpandedMap));
  }, [dispatch, isExpandedMap]);

  // page loader
  const isFetchingList = useSelector(getIsFetchingList);
  usePageLoaderOnInitial('providerList', isFetchingList);

  return (
    <>
      <SearchFormWithRef
        ref={searchFormRef}
        className={styles.searchFormWrapper}
        query={query}
        placeId={params.pageIdentifier}
        isExpandedMap={isExpandedMap}
        onUpdateListView={onUpdateListView}
        onRemoveCoupon={onRemoveCoupon}
        filterFormProps={
          !isMobile
            ? { maxPrice, minPrice, starRating, reviewRating, sortKey, features, freeCancellation }
            : {}
        }
        setSortKey={setSortKey}
        shouldOpenDatePickerOnMount={isSSR}
      />

      <div className={styles.contentWrapper} data-testid="providerListPage-content-wrapper">
        {!isMobile ? (
          <FiltersAndList
            filtersInListProps={{
              setMaxPrice,
              setMinPrice,
              setStarRating,
              setReviewRating,
              onCheckboxChanged,
              onPriceRangeChanged,
              onClearClick,
              onSearch,
            }}
            filterFormProps={{
              starRating,
              reviewRating,
              sortKey,
              features,
              freeCancellation,
              maxPrice: appliedMax,
              minPrice: appliedMin,
            }}
            onUpdateListView={onUpdateListView}
            isExpandedMap={isExpandedMap}
            query={query}
            params={params}
            onRemoveCoupon={onRemoveCoupon}
            newQuery={newQuery}
          />
        ) : (
          <ListAndMap
            isExpandedMap={isExpandedMap}
            query={query}
            params={params}
            onRemoveCoupon={onRemoveCoupon}
          />
        )}

        {!isMobile && <Breadcrumb className={styles.breadcrumb} placeLayers={placeLayers} />}
      </div>
    </>
  );
}

ProviderListPage.defaultProps = {
  params: {
    pageIdentifier: '',
  },
};

export default ProviderListPage;
